/**
 * Page-aware stamping (T-137, D-028).
 *
 * A certificate arrives as a certificate face, overleaf notes, continuation sheets and
 * printed copies, in no fixed order (AANZFTA: face, overleaf, continuation; ChAFTA: face,
 * overleaf, face, overleaf; KAFTA: original, continuation, copy, continuation). A mark tied
 * to a page NUMBER cannot serve them, so marks belong to a page KIND and every page of a real
 * certificate is classified before stamping.
 *
 * Everything here is pure, so the engine, the Approve dialog's preview and the editor's proof
 * all use one definition of "which page is which".
 */
import { z } from 'zod';
export const PAGE_KINDS = ['CERTIFICATE', 'CONTINUATION', 'OVERLEAF'];
export const pageKindSchema = z.enum(PAGE_KINDS);
/** A mark's page: a kind, or FIXED — the layout's literal page number, as before T-137. */
export const SLOT_PAGE_KINDS = ['FIXED', ...PAGE_KINDS];
export const slotPageKindSchema = z.enum(SLOT_PAGE_KINDS);
/** One entry per template page: what that page of the sample is, or null if untagged. */
export const templatePageKindsSchema = z.array(pageKindSchema.nullable()).max(60);
export const PAGE_KIND_LABEL = {
    CERTIFICATE: 'Certificate face',
    CONTINUATION: 'Continuation sheet',
    OVERLEAF: 'Overleaf notes',
};
/**
 * A layout stamps by kind once an administrator has tagged a template page as the
 * certificate face. Until then it stamps by fixed page number, exactly as before — which is
 * what keeps every existing layout printing identically (a KAFTA layout with its own marks
 * on page 2 would otherwise get the certificate-face marks there too).
 */
export function stampsByKind(templatePageKinds) {
    return Boolean(templatePageKinds?.includes('CERTIFICATE'));
}
/**
 * The face is implied: page 1, when continuation sheets are tagged but no face is.
 *
 * An administrator tags what is *different* — the continuation sheet, the overleaf — and
 * reasonably leaves page 1 alone, because page 1 is obviously the certificate. Requiring the
 * face tag as well left a tagged continuation sheet silently stamping by fixed page, with no
 * way to place its marks. Classification already assumes page 1 is the face (FIRST_PAGE).
 * Overleaf-only tags are left alone: fixed-page stamping already skips those pages.
 */
export function withImpliedFace(templatePageKinds) {
    if (!templatePageKinds || templatePageKinds.length === 0)
        return templatePageKinds ?? null;
    if (templatePageKinds.includes('CERTIFICATE') || !templatePageKinds.includes('CONTINUATION')) {
        return templatePageKinds;
    }
    if (templatePageKinds[0] !== null && templatePageKinds[0] !== undefined)
        return templatePageKinds;
    return ['CERTIFICATE', ...templatePageKinds.slice(1)];
}
/** What a mark on template page `page` belongs to. */
export function slotPageKind(page, templatePageKinds) {
    if (!stampsByKind(templatePageKinds))
        return 'FIXED';
    return templatePageKinds?.[page - 1] ?? 'FIXED';
}
/**
 * Where each kind's marks are edited: the FIRST template page tagged with it. Any other page
 * of the same kind (a printed copy in the sample, a second continuation sheet) repeats them.
 */
export function primaryTemplatePage(kind, templatePageKinds) {
    const index = templatePageKinds.indexOf(kind);
    return index >= 0 ? index + 1 : null;
}
export const pagePlanEntrySchema = z.object({
    page: z.number().int().min(1),
    kind: pageKindSchema,
    source: z.enum(['TEMPLATE', 'FIRST_PAGE', 'COPY_OF_PAGE', 'UNRECOGNISED', 'OPERATOR']),
    /** False when a person should look: shown flagged in the Approve dialog. */
    confident: z.boolean(),
    reason: z.string(),
});
/** The words on a page that identify it: lower-case, three letters or more, no numbers. */
export function pageWords(text) {
    return new Set((text ?? '').toLowerCase().match(/[a-z]{3,}/g) ?? []);
}
/** |A ∩ B| / |A ∪ B| */
export function jaccard(a, b) {
    if (a.size === 0 && b.size === 0)
        return 0;
    let shared = 0;
    for (const w of a)
        if (b.has(w))
            shared += 1;
    return shared / (a.size + b.size - shared);
}
/** Below this a page has too little printed text to be compared at all (a scan, a picture). */
const MIN_WORDS = 12;
/** A template match must score at least this… */
const MATCH_MIN = 0.3;
/** …and beat the next-best kind by this much, or it is not a match. */
const MATCH_MARGIN = 0.08;
/** Two pages of one certificate this alike are the same page printed twice (ORIGINAL / COPY 1). */
const COPY_MIN = 0.6;
/**
 * Decides what each page of a certificate is.
 *
 * In order, per page:
 *  1. Its printed text matches a tagged template page clearly → that page's kind. The text
 *     compared is the form's printed labels (the PDF text layer), so the exporter's data
 *     does not muddy it.
 *  2. Page 1 → certificate face. Every certificate starts with it.
 *  3. Near-identical to an earlier page of the same document → that page's kind. Printed
 *     copies differ from the original by a word or two.
 *  4. Otherwise → continuation sheet, flagged, never blocking (D-028).
 *
 * Measured on the sample certificates before this was written: within a form family the
 * right kind wins by a wide margin (0.98 v 0.56, 1.00 v 0.76, 0.88 v 0.24). Some issued
 * certificates print the form's labels as a picture with only the typed data as text
 * (the ChAFTA and KAFTA samples); those match nothing and fall to rules 2–4, which is why
 * those rules exist.
 */
export function classifyPages(input) {
    const byKind = new Map();
    input.templatePageKinds.forEach((kind, i) => {
        if (!kind)
            return;
        const words = byKind.get(kind) ?? new Set();
        for (const w of pageWords(input.templateTexts[i]))
            words.add(w);
        byKind.set(kind, words);
    });
    const usableKinds = [...byKind.entries()].filter(([, words]) => words.size >= MIN_WORDS);
    const pages = input.pageTexts.map(pageWords);
    const plan = [];
    pages.forEach((words, i) => {
        const page = i + 1;
        if (words.size >= MIN_WORDS && usableKinds.length > 0) {
            const scored = usableKinds
                .map(([kind, kindWords]) => ({ kind, score: jaccard(words, kindWords) }))
                .sort((a, b) => b.score - a.score);
            const [best, next] = scored;
            if (best && best.score >= MATCH_MIN && best.score - (next?.score ?? 0) >= MATCH_MARGIN) {
                plan.push({
                    page,
                    kind: best.kind,
                    source: 'TEMPLATE',
                    confident: true,
                    reason: `Matches the template's ${PAGE_KIND_LABEL[best.kind].toLowerCase()} (${Math.round(best.score * 100)}% alike)`,
                });
                return;
            }
        }
        if (page === 1) {
            plan.push({ page, kind: 'CERTIFICATE', source: 'FIRST_PAGE', confident: true, reason: 'The first page is the certificate face' });
            return;
        }
        if (words.size >= MIN_WORDS) {
            let copyOf = null;
            for (let j = 0; j < i; j += 1) {
                const score = jaccard(words, pages[j]);
                if (score >= COPY_MIN && (!copyOf || score > copyOf.score))
                    copyOf = { page: j + 1, score };
            }
            if (copyOf) {
                const earlier = plan[copyOf.page - 1];
                plan.push({
                    page,
                    kind: earlier.kind,
                    source: 'COPY_OF_PAGE',
                    confident: earlier.confident,
                    reason: `Repeats page ${copyOf.page} (${Math.round(copyOf.score * 100)}% alike) — a printed copy`,
                });
                return;
            }
        }
        plan.push({
            page,
            kind: 'CONTINUATION',
            source: 'UNRECOGNISED',
            confident: false,
            reason: words.size < MIN_WORDS
                ? 'No readable text to identify it — stamped as a continuation sheet'
                : 'Not recognised — stamped as a continuation sheet',
        });
    });
    return plan;
}
/** The plan for a layout that does not stamp by kind: every page is what its number says. */
export function fixedPagePlan(pageCount, overleafPages) {
    return Array.from({ length: pageCount }, (_, i) => ({
        page: i + 1,
        kind: overleafPages.includes(i + 1) ? 'OVERLEAF' : 'CERTIFICATE',
        source: 'TEMPLATE',
        confident: true,
        reason: 'Fixed page layout',
    }));
}
/**
 * The plan for a layout's own template, as tagged in the editor: each tagged page is what it
 * was tagged as. Untagged pages are left out — only FIXED marks print on them.
 */
export function templatePagePlan(templatePageKinds) {
    return templatePageKinds.flatMap((kind, i) => kind ? [{ page: i + 1, kind, source: 'TEMPLATE', confident: true, reason: 'Tagged in the editor' }] : []);
}
/** An operator's corrections, applied over the computed plan. */
export function applyOperatorKinds(plan, kinds) {
    if (!kinds)
        return plan;
    return plan.map((entry, i) => {
        const chosen = kinds[i];
        if (!chosen || chosen === entry.kind)
            return entry;
        return { ...entry, kind: chosen, source: 'OPERATOR', confident: true, reason: `Set to ${PAGE_KIND_LABEL[chosen].toLowerCase()} by the operator` };
    });
}
/**
 * Turns the layout's marks into the marks for THIS certificate.
 *
 *  - FIXED marks go on their page number, if the certificate has that page; otherwise they
 *    are skipped and reported (they used to refuse the approval outright).
 *  - Kind marks go on every page of their kind: certificate-face marks on the original and
 *    each printed copy, continuation marks on every continuation sheet.
 *  - Overleaf marks only where the layout allows the overleaf to be stamped.
 *  - `{PAGE}` and `{PAGES}` in a text mark become "3" and "5" on the page it lands on.
 */
export function expandMarks(marks, plan, options = {}) {
    const pageCount = options.pageCount ?? plan.length;
    const out = [];
    const skipped = [];
    for (const mark of marks) {
        if (mark.pageKind === 'FIXED') {
            if (mark.page >= 1 && mark.page <= pageCount)
                out.push(onPage(mark, mark.page, pageCount));
            else
                skipped.push(mark);
            continue;
        }
        if (mark.pageKind === 'OVERLEAF' && !options.stampOverleaf)
            continue;
        const targets = plan.filter((p) => p.kind === mark.pageKind).map((p) => p.page);
        if (targets.length === 0)
            continue;
        for (const page of targets)
            out.push(onPage(mark, page, pageCount));
    }
    const marked = new Set(out.map((m) => m.page));
    const unstampedPages = plan
        .filter((p) => !marked.has(p.page) && !(p.kind === 'OVERLEAF' && !options.stampOverleaf))
        .map((p) => p.page);
    return { marks: out, skipped, unstampedPages };
}
function onPage(mark, page, pageCount) {
    const text = typeof mark.text === 'string' ? mark.text.replaceAll('{PAGE}', String(page)).replaceAll('{PAGES}', String(pageCount)) : mark.text;
    return { ...mark, page, ...(text !== undefined ? { text } : {}) };
}
//# sourceMappingURL=page-kinds.js.map